import { useMemo } from 'react';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { EventPageType } from '@graphql/generated-base';
import type { PageKind } from '@graphql/generated-base';
import type { RoutesPaths } from '@shared/core';
import useQueryString from '@shared/utils/hooks/useQueryString';
import { getMigratedPageSlug, isUserGeneratedMigratedSquatter, toCanonicalMigratedSlug } from '@shared/utils/migratedCustomPages';
import type { MinimalPage } from '@shared/hooks/useDefaultPage';
import { useDefaultPage } from '@shared/hooks/useDefaultPage';
import { ALLOWLIST_CUSTOM_PAGE_SLUGS, CUSTOM_PAGE_PATH } from './GuestSite.constants';

const SUPPORTED_PAGES = [
  EventPageType.welcome,
  EventPageType.story,
  EventPageType.vip,
  EventPageType.faq,
  EventPageType.schedule,
  EventPageType.travel,
  EventPageType.moments,
  EventPageType.registry,
  EventPageType.rsvp,
  EventPageType.tidbits,
  EventPageType.custom,
] as const;

const supportedPagesSet = new Set<EventPageType>(SUPPORTED_PAGES);

export type SupportedEventPages = (typeof SUPPORTED_PAGES)[number];

export type EventPageRoutePaths = Partial<RoutesPaths<SupportedEventPages>>;

export const useEventPageRouting = (eventHandle: string, pages: EventPageFragment[]): EventPageRoutePaths => {
  return useMemo(() => {
    if (!eventHandle || !eventHandle.trim().length) {
      return {};
    }
    return pages.reduce((acc, page) => {
      if (supportedPagesSet.has(page.type)) {
        // For migrated pages (like VIP) when feature flag is ON, use the canonical type slug (e.g., 'vip')
        // instead of the page's pageSlug (e.g., 'weddingparty')
        const migratedSlug = getMigratedPageSlug(page.type);
        // For custom pages acting as a migrated replacement, pageSlug may be an EventPageType value
        // (e.g. 'registry' for the registrylinks migration). Canonicalize back to the URL segment.
        const canonicalCustomSlug = page.type === EventPageType.custom ? toCanonicalMigratedSlug(page.pageSlug) : undefined;

        const slug = migratedSlug || canonicalCustomSlug || page.pageSlug;

        acc[page.type as SupportedEventPages] = {
          path: `/${eventHandle}/${slug}`,
          goToPath: () => slug,
        };
      }
      return acc;
    }, {} as EventPageRoutePaths);
  }, [eventHandle, pages]);
};

export const useEventPageRoute = (eventHandle: string, pages?: ReadonlyArray<MinimalPage>) => {
  const { isDefaultPage: checkIsDefaultPage } = useDefaultPage(pages);

  const queryString = useQueryString();

  const eventPageRoute = {
    path: `/:eventHandle`,
    goToPath: (type: EventPageType, pageSlug: string) => {
      // Default page (welcome or first page) uses root URL (no slug)
      if (checkIsDefaultPage(pageSlug, type)) {
        let url = `/${eventHandle}`;

        if (queryString) {
          url += `${queryString}`;
        }

        return url;
      }

      // Migrated registrylinks pages carry pageSlug='registry' in data; the canonical URL segment
      // is 'registrylinks'. For all other migrations the two are identical.
      const canonicalMigratedSlug = toCanonicalMigratedSlug(pageSlug);
      const canonicalSlug = canonicalMigratedSlug ?? pageSlug;

      // Allowlisted pages are using the short route format (/:eventHandle/:slug) despite being custom pages
      const isAllowlistedSlug = ALLOWLIST_CUSTOM_PAGE_SLUGS.has(pageSlug);

      // CORE-6456: a userGenerated page squatting a migrated slug (e.g. user-named "Schedule")
      // must always use the generic /page/{pageSlug} form, regardless of flags. Look up the full
      // page in `pages` (matched by type + pageSlug) to read its contentType, which goToPath's
      // arguments don't carry.
      const squatterPage = pages?.find(p => p.type === type && p.pageSlug === pageSlug);
      const isSquatter = !!squatterPage && isUserGeneratedMigratedSquatter(squatterPage);

      // Only custom pages that are not allowlisted, not flag-migrated, and not rollback-aware
      // data-migrated take the /page/{pageSlug} form.
      const useCustomPath = isSquatter || (type === EventPageType.custom && !isAllowlistedSlug && !canonicalMigratedSlug);

      let url = useCustomPath ? `/${eventHandle}/${CUSTOM_PAGE_PATH}/${pageSlug}` : `/${eventHandle}/${canonicalSlug}`;

      if (queryString) {
        url += `${queryString}`;
      }

      return url;
    },
  };
  return eventPageRoute;
};

// CORE-6456: the admin preview-nav message (`useSetActiveWebsitePreviewPage`) carries the edited
// page's slug + `pageType`. A userGenerated squatter (a custom page whose slug collides with a
// native/migrated slug, e.g. a user-named "Schedule") shares its slug with the native page, so the
// slug-keyed `eventPageRoutes` lookup in `PreviewListener` resolves the *native* route and the
// preview shows the wrong page. When the edited page is such a squatter, return its generic
// `/page/<slug>` guest path so the preview tracks the user's page. Returns `undefined` for every
// other page so the caller falls back to its normal route lookup.
//
// PRNT-4591: since migration, a `systemCustom` and a `userCustom` container can share one slug, so
// the event carries *two* `custom` pages with the same `pageSlug` and `pages.find` returns whichever
// the list orders first — for `[systemCustom, userCustom]` that is the system row, which is not a
// squatter, so the couple's page would be sent to the native `/<slug>` URL. `pageKind` is the
// editor's answer to "which container did I open": when the sender supplies it, it decides, and the
// ambiguous list lookup is skipped entirely.
export const getCustomSquatterPreviewPath = (
  eventHandle: string,
  pageType: EventPageType | undefined,
  pageSlug: string,
  pages: ReadonlyArray<Pick<EventPageFragment, 'type' | 'pageSlug' | 'pageKind' | 'contentType'>>,
  pageKind?: PageKind | null,
): string | undefined => {
  if (pageType !== EventPageType.custom) return undefined;
  // Legacy senders (the Angular editor, and any admin tab loaded before this shipped) omit
  // `pageKind`; they keep the list lookup so their behaviour is unchanged.
  const editedPage = pageKind ? { pageSlug, pageKind } : pages.find(page => page.type === EventPageType.custom && page.pageSlug === pageSlug);
  if (!editedPage || !isUserGeneratedMigratedSquatter(editedPage)) return undefined;
  return `/${eventHandle}/${CUSTOM_PAGE_PATH}/${pageSlug}`;
};
